import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Empty } from '@prio365/prio365-react-library';
import {
  EFinancialDocumentType,
  FinancialDocumentAllocation,
} from '../../../../../../models/IncomingInvoices';
import { makePrioStyles } from '../../../../../../theme/utils';
import { getCurrencySymbol } from '../../../../../../util/helpers';
import { useDeleteAllocationsMutation } from '../hooks/useDeleteAllocationMutation';
import AllocationCard, {
  roundTo,
} from '../incomingInvoiceComponents/AllocationCard';
import CreateAllocationButton from '../incomingInvoiceComponents/CreateAllocationButton';
import { useIncomingInvoiceDrawerContext } from '../incomingInvoiceComponents/IncomingInvoiceContextProvider';
import { useAllocationState } from '../hooks/useAllocationState';
import { useAllocations } from '../hooks/useAllocations';

const useStyles = makePrioStyles((theme) => ({
  root: {
    display: 'flex',
    gap: theme.spacing.regular,
    flexDirection: 'column',
    width: '100%',
  },
  allocationWrapper: {
    maxHeight: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.small,
    overflowY: 'auto',
  },
  allocationTotalLabel: {
    fontWeight: theme.font.fontWeight.bold,
    color: theme.colors.application.typography.default,
    fontSize: theme.font.fontSize.small,
  },
}));

interface IncomingInvoicesDrawerAllocationStepProps {
  className?: string;
  amountToTransfer: number;
  allocationStepOptional?: boolean;
  mode?: 'edit' | 'view' | 'review';
}

export const IncomingInvoicesDrawerAllocationStep: React.FC<
  IncomingInvoicesDrawerAllocationStepProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    className,
    amountToTransfer,
    allocationStepOptional = false,
    mode = 'edit',
  } = props;

  const classes = useStyles();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const {
    form,
    setInvoiceAllocations,
    setInitialAllocations,
    financialDocumentId,
    setInvoiceAmountAllocated,
    setAllocationsValid,
  } = useIncomingInvoiceDrawerContext();

  const sharedWithProjectIds = useMemo(
    () => form.getFieldValue('sharedWithProjectIds') || [],
    [form]
  );

  const { data: queriedAllocations } = useAllocations(
    sharedWithProjectIds.length > 0 ? sharedWithProjectIds[0] : '', // <-- Abfrage mit irgendeiner ProjektId gibt alle Allocations des financialDocuments zurück. Abfrage für alle Projects sind unnötige Calls
    financialDocumentId
  );

  const initialAllocations = useMemo(() => {
    let allocations: Partial<FinancialDocumentAllocation>[] = [];

    if (queriedAllocations && queriedAllocations.length > 0)
      allocations = queriedAllocations;
    else if (!allocationStepOptional && mode !== 'review') {
      // Only create initial allocation if not optional and not in review mode
      allocations = [
        {
          financialDocumentId: financialDocumentId,
          financialDocumentType: EFinancialDocumentType.IncomingInvoice,
          projectId: undefined,
          projectPhaseId: undefined,
          amount: amountToTransfer || 0,
        },
      ];
    }

    const uniqueAllocations = allocations.filter(
      (alloc, idx, arr) =>
        !alloc.financialDocumentAllocationId ||
        arr.findIndex(
          (a) =>
            a.financialDocumentAllocationId ===
            alloc.financialDocumentAllocationId
        ) === idx
    );

    return uniqueAllocations;
  }, [
    queriedAllocations,
    financialDocumentId,
    amountToTransfer,
    allocationStepOptional,
    mode,
  ]);

  const {
    invoiceAllocations,
    addAllocation,
    updateAllocation,
    removeAllocationLocally,
  } = useAllocationState({
    initialAllocations,
    financialDocumentId: financialDocumentId,
  });

  const deleteAllocationsMutation = useDeleteAllocationsMutation();
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const getNotAllocatedSum = useMemo(() => {
    const allocatedSum = invoiceAllocations?.reduce((sum, alloc) => {
      if (typeof alloc.amount === 'number') {
        return sum + alloc.amount;
      }
      return sum;
    }, 0);

    const totalSum = amountToTransfer || 0;

    return roundTo(totalSum - allocatedSum);
  }, [amountToTransfer, invoiceAllocations]);

  const removeAllocation = async (index: number) => {
    const alloc = invoiceAllocations[index];

    if (alloc.financialDocumentAllocationId) {
      await deleteAllocationsMutation.mutateAsync({
        projectId: alloc.projectId || '',
        financialDocumentId: alloc.financialDocumentId || '',
        allocationIds: [alloc.financialDocumentAllocationId],
      });

      setInvoiceAllocations(
        (invoiceAllocations ?? []).filter(
          (a: FinancialDocumentAllocation) =>
            a.financialDocumentAllocationId !==
            alloc?.financialDocumentAllocationId
        )
      );
    }

    removeAllocationLocally(index);
  };

  const getMaxAmountForAllocation = (currentIndex: number) => {
    const otherAllocationsSum = (invoiceAllocations ?? [])
      .filter((_, index) => index !== currentIndex)
      .reduce((sum, alloc) => sum + (alloc.amount || 0), 0);

    return (amountToTransfer || 0) - otherAllocationsSum;
  };

  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const projectIds = invoiceAllocations
      ?.map((a) => a.projectId)
      .filter(Boolean);
    form.setFieldsValue({
      sharedWithProjectIds: Array.from(new Set(projectIds)),
    });
  }, [invoiceAllocations, form]);

  useEffect(() => {
    setInitialAllocations(initialAllocations);
  }, [setInitialAllocations, initialAllocations]);

  useEffect(() => {
    if (getNotAllocatedSum === 0) {
      setInvoiceAmountAllocated(true);
    } else {
      setInvoiceAmountAllocated(false);
    }
  }, [getNotAllocatedSum, setInvoiceAmountAllocated]);

  useEffect(() => {
    setAllocationsValid(
      (invoiceAllocations ?? []).every(
        (allocation) => !!allocation.projectId && allocation.amount !== 0
      )
    );
  }, [setAllocationsValid, invoiceAllocations]);
  //#endregion

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.allocationWrapper}>
        {invoiceAllocations && invoiceAllocations.length > 0 ? (
          invoiceAllocations.map((allocation, index) => (
            <AllocationCard
              key={index}
              allocation={allocation}
              maxAmount={getMaxAmountForAllocation(index)}
              onChange={(values) => updateAllocation(index, values)}
              onRemove={() => removeAllocation(index)}
              allocationStepOptional={allocationStepOptional}
            />
          ))
        ) : (
          <Empty
            customMessage={t(
              'accounting:incomingInvoices.drawerSteps.allocations.emptyState'
            )}
          />
        )}
      </div>
      {getNotAllocatedSum !== 0 ? (
        <span className={classes.allocationTotalLabel}>
          {t(
            'accounting:incomingInvoices.drawerSteps.allocations.allocationTotalUnassigned',
            {
              formatted: `${getNotAllocatedSum.toFixed(2)} ${getCurrencySymbol(
                form.getFieldValue('currency') || 'EUR',
                i18n.language
              )}`,
            }
          )}
        </span>
      ) : (
        <span className={classes.allocationTotalLabel}>
          {t(
            'accounting:incomingInvoices.drawerSteps.allocations.allocationTotalAssigned'
          )}
        </span>
      )}
      {getNotAllocatedSum !== 0 && (
        <CreateAllocationButton
          onClick={() => addAllocation(roundTo(getNotAllocatedSum))}
        />
      )}
    </div>
  );
};

export default IncomingInvoicesDrawerAllocationStep;
